import type { ConversationItem } from '@vivport/backend/types';

export function toSpeakableText(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/<[^>]+>/g, ' ')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/[*_~|>]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Sentence-level TTS: fragments under this length are merged forward into their
// neighbour so the synth is never asked for confetti.
export const SENTENCE_MIN_CHARS = 25;

// Splits text into speakable sentences on . ! ? followed by whitespace/end,
// preserving concatenation (join('') === input) so streaming callers can track
// spoken offsets in input space. Does not break decimals (3.5) or single-letter
// initials (J. Smith); merges fragments under SENTENCE_MIN_CHARS forward.
export function splitSpeakableSentences(text: string): string[] {
  const pieces: string[] = [];
  let start = 0;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch !== '.' && ch !== '!' && ch !== '?') continue;
    const next = text[i + 1];
    if (next !== undefined && !/\s/.test(next)) continue;
    if (ch === '.') {
      const prev = text[i - 1];
      const prev2 = text[i - 2];
      if (prev !== undefined && /[A-Za-z]/.test(prev) && (prev2 === undefined || /\s/.test(prev2))) {
        continue;
      }
    }
    let end = i + 1;
    while (end < text.length && /\s/.test(text[end] as string)) end += 1;
    pieces.push(text.slice(start, end));
    start = end;
    i = end - 1;
  }
  if (start < text.length) pieces.push(text.slice(start));
  const merged: string[] = [];
  let carry = '';
  for (const piece of pieces) {
    carry += piece;
    if (carry.trim().length >= SENTENCE_MIN_CHARS) {
      merged.push(carry);
      carry = '';
    }
  }
  if (carry) {
    if (merged.length > 0) {
      merged[merged.length - 1] += carry;
    } else {
      merged.push(carry);
    }
  }
  return merged;
}

// Every TTS request must fit the active provider's cap: a sentence over it is split on
// whitespace into greedy pieces, and a single token longer than the cap is sliced.
export function capSpeakableSentences(sentences: string[], maxChars: number): string[] {
  if (maxChars < 1) throw new Error(`capSpeakableSentences needs a positive cap, got ${maxChars}`);
  const capped: string[] = [];
  for (const sentence of sentences) {
    if (sentence.length <= maxChars) {
      capped.push(sentence);
      continue;
    }
    let piece = '';
    for (const token of sentence.split(/\s+/).filter(Boolean)) {
      let word = token;
      while (word.length > maxChars) {
        if (piece) {
          capped.push(piece);
          piece = '';
        }
        capped.push(word.slice(0, maxChars));
        word = word.slice(maxChars);
      }
      const candidate = piece ? `${piece} ${word}` : word;
      if (candidate.length <= maxChars) {
        piece = candidate;
      } else {
        capped.push(piece);
        piece = word;
      }
    }
    if (piece) capped.push(piece);
  }
  return capped;
}

// Interjection floor during playback: a non-matching fragment at or above this
// combined length triggers barge-in; below it the fragment accumulates without
// cancelling. 4 is safe because residual echo on AEC-less platforms garbles
// TOWARD script words, which matchesSpokenScript already absorbs — short
// NON-matching fragments are overwhelmingly genuine member speech ("no", "wait").
export const INTERRUPT_TRANSCRIPT_MIN_LENGTH = 4;
// Fallback defence only (see readme/voice.md): the browser's
// acoustic echo canceller now has the TTS reference signal, so this tail is residual
// insurance for AEC-less platforms rather than the primary suppression window.
export const SPEECH_ECHO_TAIL_MS = 400;
export const ECHO_MATCH_THRESHOLD = 0.75;

const DIGIT_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

export function normalizeSpokenWords(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => (/^[0-9]$/.test(word) ? DIGIT_WORDS[Number(word)] : word));
}

export interface SpokenScript {
  words: Set<string>;
  joined: string;
}

export function editDistanceAtMostOne(a: string, b: string): boolean {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  const short = a.length <= b.length ? a : b;
  const long = a.length <= b.length ? b : a;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < short.length && j < long.length) {
    if (short[i] === long[j]) {
      i += 1;
      j += 1;
      continue;
    }
    edits += 1;
    if (edits > 1) return false;
    if (short.length === long.length) {
      i += 1;
      j += 1;
    } else {
      j += 1;
    }
  }
  return edits + (long.length - j) <= 1;
}

// STT garbles echo of the script ("Vivport" → "vivport", "Dirk" → "dirt",
// "call" → "called"), so a heard word counts as echo on set membership, substring
// containment either way, or single-edit distance from a script word.
export function wordMatchesScript(word: string, script: SpokenScript): boolean {
  if (script.words.has(word)) return true;
  if (word.length >= 3 && script.joined.includes(word)) return true;
  if (word.length < 4) return false;
  for (const scriptWord of script.words) {
    if (scriptWord.length < 4) continue;
    if (word.includes(scriptWord) || scriptWord.includes(word)) return true;
    if (editDistanceAtMostOne(word, scriptWord)) return true;
  }
  return false;
}

export function matchesSpokenScript(heard: string, script: SpokenScript): boolean {
  const words = normalizeSpokenWords(heard);
  if (words.length === 0) return true;
  const matched = words.filter((word) => wordMatchesScript(word, script)).length;
  return matched / words.length >= ECHO_MATCH_THRESHOLD;
}

export function hasSpokenText(
  item: ConversationItem,
): item is ConversationItem & { spokenText: string } {
  return (
    'spokenText' in item &&
    typeof (item as { spokenText?: unknown }).spokenText === 'string' &&
    (item as { spokenText: string }).spokenText.length > 0
  );
}
